import React, { useState } from 'react';
import {
  Container,
  Title,
  Text,
  TextInput,
  Button,
  Group,
  Box,
  Paper,
  Stack,
  Alert,
  Flex
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { Header } from '../../landing/Header';
import { Footer } from '../../landing/Footer';
import { useNavigate } from 'react-router-dom';

export const Waitlist: React.FC = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const form = useForm({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      firstName: (value) => (value.trim().length > 0 ? null : 'First name is required'),
      lastName: (value) => (value.trim().length > 0 ? null : 'Last name is required'),
    },
  });

  const handleSubmit = async (values: { email: string; firstName: string; lastName: string }) => {
    try {
      setError('');
      console.log('Waitlist submission:', values);
      
      // Make the API call to join the waitlist
      const response = await fetch('https://api.sortmyshoebox.com/user/join-waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: values.email,
          name: `${values.firstName} ${values.lastName}`,
          firstName: values.firstName,
          lastName: values.lastName
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to join waitlist. Please try again.');
      }
    } catch (err) {
      console.error('Error joining waitlist:', err);
      setError('There was an error connecting to our service. Please try again later.');
    }
  };

  return (
    <Flex
      direction="column"
      style={{
        minHeight: '100vh',
      }}
    >
      <Header
        onLoginClick={() => navigate('/auth/login')}
        onRegisterClick={() => navigate('/waitlist')}
        scrollToSection={(sectionId) => {
          // Navigate to the landing page and then scroll to the section
          navigate('/');
          // Use setTimeout to ensure navigation completes before scrolling
          setTimeout(() => {
            const element = document.getElementById(sectionId);
            if (element) {
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }, 100);
        }}
      />

      <Container size="md" py="xl" style={{ flex: 1, marginTop: 100 }}>
        <Paper shadow="md" radius="lg" p={{ base: 'xl', sm: '2xl' }} withBorder>
          <Stack gap="xl">
            <Title order={1} ta="center" style={{ fontFamily: 'Bebas Neue' }}>
              JOIN OUR WAITLIST
            </Title>
            
            {/* <Text size="lg" ta="center" mb="md">
              We're launching on May 1st, 2025! Sign up now to get early access and be the first to organize your photo collection with our AI-powered platform.
            </Text> */}
            
            <Text size="md" ta="center" mb="lg" c="dimmed">
              Enter your email and name below to join our waitlist. You'll receive updates about our launch and be among the first to try Sort My Shoebox when it's ready.
            </Text>

            {submitted ? (
              <Alert 
                icon={<IconCheck size="1.1rem" />} 
                title="Thank you for joining our waitlist!" 
                color="green"
                radius="md"
              >
                <Stack gap="md">
                  <Text>We'll notify you when Sort My Shoebox launches on May 1st, 2025. In the meantime, check your email for a confirmation.</Text>
                  
                  <Button 
                    onClick={() => navigate('/')} 
                    variant="light" 
                    size="sm"
                  >
                    Return to Home
                  </Button>
                </Stack>
              </Alert>
            ) : (
              <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                  {error && (
                    <Alert 
                      icon={<IconAlertCircle size="1.1rem" />} 
                      title="Error" 
                      color="red"
                      radius="md"
                    >
                      {error}
                    </Alert>
                  )}
                  
                  <Group grow>
                    <TextInput
                      label="First Name"
                      placeholder="First name"
                      required
                      {...form.getInputProps('firstName')}
                    />
                    <TextInput
                      label="Last Name"
                      placeholder="Last name"
                      required
                      {...form.getInputProps('lastName')}
                    />
                  </Group>
                  
                  <TextInput
                    label="Email"
                    placeholder="your@email.com"
                    required
                    {...form.getInputProps('email')}
                  />
                  
                  <Box mt="md">
                    <Button 
                      fullWidth 
                      size="lg" 
                      type="submit"
                    >
                      Join Waitlist
                    </Button>
                  </Box>
                </Stack>
              </form>
            )}
            
            <Text size="sm" c="dimmed" ta="center">
              We respect your privacy. Your information will never be shared with third parties.
            </Text>
          </Stack>
        </Paper>
      </Container>
      
      <Footer />
    </Flex>
  );
};
